import React, { useState, useEffect, useRef } from 'react';
import { media_url } from '../../hooks/useBlog';
import { 
  FaTimes, FaPlay, FaPause, FaVolumeUp, FaVolumeMute, 
  FaChevronLeft, FaChevronRight, FaShareAlt, FaFacebookF,
  FaTwitter, FaWhatsapp, FaLink
} from 'react-icons/fa';
import './NewsVideoOverlay.scss';

const NewsVideoOverlay = ({ 
  video, 
  videoIndex, 
  totalVideos, 
  onClose, 
  onNext, 
  onPrev 
}) => {
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [progress, setProgress] = useState(0);
  const videoRef = useRef(null);
  const iframeRef = useRef(null);
  const controlsTimeout = useRef(null);

  useEffect(() => {
    // Auto-play video cuando se monta
    if (videoRef.current && !video.url) {
      videoRef.current.play();
    }

    // Ocultar controles después de 3 segundos
    resetControlsTimeout();

    return () => {
      if (controlsTimeout.current) {
        clearTimeout(controlsTimeout.current);
      }
    };
  }, [video]);

  const resetControlsTimeout = () => {
    if (controlsTimeout.current) {
      clearTimeout(controlsTimeout.current);
    }
    setShowControls(true);
    controlsTimeout.current = setTimeout(() => {
      if (isPlaying) {
        setShowControls(false);
      }
    }, 3000);
  };

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleMuteUnmute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const progress = (videoRef.current.currentTime / videoRef.current.duration) * 100;
      setProgress(progress);
    }
  };

  const handleVideoEnd = () => {
    setIsPlaying(false);
    if (videoIndex < totalVideos - 1) {
      setTimeout(() => {
        onNext();
      }, 500);
    }
  };

  const handleMouseMove = () => {
    resetControlsTimeout();
  };

  const handleShareClick = () => {
    setShowShareMenu(!showShareMenu);
  };

  const getVideoUrl = () => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/video/${video.id}`;
  };

  const handleShare = (platform) => {
    const url = getVideoUrl();
    const text = video.title || 'Mira este video';
    
    switch(platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`, '_blank');
        break;
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`, '_blank');
        break;
      case 'copy':
        navigator.clipboard.writeText(url);
        alert('Enlace copiado al portapapeles');
        break;
    }
    setShowShareMenu(false);
  };

  return (
    <div 
      className="news-video-overlay"
      onMouseMove={handleMouseMove}
      onClick={(e) => {
        if (e.target.classList.contains('news-video-overlay')) {
          onClose();
        }
      }}
    >
      {/* Progress Bar */}
      <div className="video-progress-bar">
        <div 
          className="video-progress-fill" 
          style={{ width: `${progress}%` }}
        ></div>
      </div>

      {/* Close Button */}
      <button 
        className={`overlay-close-btn ${showControls ? 'visible' : ''}`}
        onClick={onClose}
      >
        <FaTimes />
      </button>

      {/* Navigation Arrows */}
      {videoIndex > 0 && (
        <button 
          className={`overlay-nav-btn overlay-nav-prev ${showControls ? 'visible' : ''}`}
          onClick={onPrev}
        >
          <FaChevronLeft />
        </button>
      )}
      
      {videoIndex < totalVideos - 1 && (
        <button 
          className={`overlay-nav-btn overlay-nav-next ${showControls ? 'visible' : ''}`}
          onClick={onNext}
        >
          <FaChevronRight />
        </button>
      )}

      {/* Video Container */}
      <div className="video-container">
        {video.url ? (
          <iframe
            ref={iframeRef}
            src={video.url}
            title={video.title}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="video-iframe"
          />
        ) : (
          <video
            ref={videoRef}
            src={media_url(video.video)}
            className="video-player"
            onTimeUpdate={handleTimeUpdate}
            onEnded={handleVideoEnd}
            onClick={handlePlayPause}
            playsInline
          />
        )}

        {/* Video Controls Overlay */}
        <div 
          className={`video-controls-overlay ${showControls ? 'visible' : ''}`}
          onClick={handlePlayPause}
        >
          {!isPlaying && !video.url && (
            <button className="play-pause-btn large">
              <FaPlay />
            </button>
          )}
        </div>
      </div>

      {/* Bottom Controls */}
      <div className={`video-bottom-controls ${showControls ? 'visible' : ''}`}>
        <div className="video-info">
          <h3 className="video-title">{video.title}</h3>
          {video.description && (
            <p className="video-description">{video.description}</p>
          )}
        </div>

        <div className="video-actions">
          {!video.url && (
            <>
              <button 
                className="control-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handlePlayPause();
                }}
              >
                {isPlaying ? <FaPause /> : <FaPlay />}
              </button>
              
              <button 
                className="control-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleMuteUnmute();
                }}
              >
                {isMuted ? <FaVolumeMute /> : <FaVolumeUp />}
              </button>
            </>
          )}

          <div className="share-dropdown">
            <button 
              className="control-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleShareClick();
              }}
            >
              <FaShareAlt />
            </button>

            {showShareMenu && (
              <div className="share-menu">
                <button onClick={() => handleShare('facebook')}>
                  <FaFacebookF /> Facebook
                </button>
                <button onClick={() => handleShare('twitter')}>
                  <FaTwitter /> Twitter
                </button>
                <button onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp /> WhatsApp
                </button>
                <button onClick={() => handleShare('copy')}>
                  <FaLink /> Copiar enlace
                </button>
              </div>
            )}
          </div>

          <span className="video-counter">
            {videoIndex + 1} / {totalVideos}
          </span>
        </div>
      </div>
    </div>
  );
};

export default NewsVideoOverlay;
